/**
 * Runtime render identity is supplied by the active server-side tyre
 * configuration. The bridge below is deliberately limited to immutable legacy
 * class/layout identifiers while the backend rolls out native render keys.
 */

const bridge = (renderModelKey, renderLayoutKey) => Object.freeze({
  renderModelKey,
  renderLayoutKey,
});

const LEGACY_RENDER_KEY_BRIDGE = Object.freeze({
  'car|car-4-wheel|1': bridge('car', 'car-4-wheel-v1'),
  'bus|bus-6-wheel|1': bridge('bus', 'bus-6-wheel-v1'),
  'jcb|jcb-4-wheel|1': bridge('jcb', 'jcb-4-wheel-v1'),
  'mixer|mixer-6-wheel|1': bridge('mixer', 'mixer-6-wheel-v1'),
  'mixer|mixer-10-wheel|1': bridge('mixer', 'mixer-10-wheel-v1'),
  'trailer|trailer-8-wheel|1': bridge('trailer', 'trailer-8-wheel-v1'),
  'trailer|trailer-12-wheel|1': bridge('trailer', 'trailer-12-wheel-v1'),
  'truck|truck-4-wheel|1': bridge('truck', 'truck-4-wheel-v1'),
  'truck|truck-6-wheel|1': bridge('truck', 'truck-6-wheel-v1'),
  'truck|truck-10-wheel|1': bridge('truck', 'truck-10-wheel-v1'),
  'truck|truck-12-wheel|1': bridge('truck', 'truck-12-wheel-v1'),
  'truck|truck-16-wheel|1': bridge('truck', 'truck-16-wheel-v1'),
});

const valueOf = (value) => (value === undefined || value === null ? undefined : value);

const configurationParts = (configuration = {}) => {
  const layout = configuration.layout_template || {};
  const layoutClass = layout.vehicle_class || {};
  const configurationClass = configuration.vehicle_class || {};
  const vehicleClass = { ...layoutClass, ...configurationClass };

  return {
    classKey: valueOf(configuration.vehicle_class_code || vehicleClass.code),
    classLabel: valueOf(vehicleClass.name),
    layoutCode: valueOf(layout.code),
    layoutVersion: valueOf(layout.version),
    layoutLabel: valueOf(layout.name),
    renderModelKey: valueOf(
      configuration.render_model_key
      || layout.render_model_key
      || vehicleClass.render_model_key
    ),
    renderLayoutKey: valueOf(configuration.render_layout_key || layout.render_layout_key),
  };
};

const unmapped = (parts, reason) => ({
  ...parts,
  source: 'unmapped',
  reason,
});

const bridgeKeyFor = ({ classKey, layoutCode, layoutVersion }) => (
  `${classKey || ''}|${layoutCode || ''}|${layoutVersion || ''}`
);

/**
 * Normalize native or legacy render metadata without consulting free text.
 * `bridgeEntries` is injectable so the migration seam can be tested independently.
 */
const resolveRenderIdentity = (configuration, bridgeEntries = LEGACY_RENDER_KEY_BRIDGE) => {
  const parts = configurationParts(configuration);

  if (parts.renderModelKey && parts.renderLayoutKey) {
    return { ...parts, source: 'native' };
  }

  if (!parts.layoutCode) return unmapped(parts, 'missing_layout_code');
  if (!parts.classKey) return unmapped(parts, 'missing_class_code');
  if (parts.layoutVersion === undefined) return unmapped(parts, 'missing_layout_version');

  const entry = bridgeEntries[bridgeKeyFor(parts)];
  if (!entry) return unmapped(parts, 'no_legacy_bridge');

  return {
    ...parts,
    renderModelKey: entry.renderModelKey,
    renderLayoutKey: entry.renderLayoutKey,
    source: 'bridge',
    reason: 'legacy_render_key_bridge',
  };
};

export {
  LEGACY_RENDER_KEY_BRIDGE,
  bridgeKeyFor,
  resolveRenderIdentity,
};
